import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  fetchStats,
  fetchTrips,
  clearToken,
  formatDate,
  STATUS_LIST,
} from "./adminApi";

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block whitespace-nowrap rounded-full border px-3 py-1 font-sans text-[0.65rem] uppercase tracking-[0.14em] ${
      status === "NEW" ? "border-teal text-teal" : "border-charcoal text-sand"
    }`}
  >
    {status}
  </span>
);

const AdminHeader = () => {
  const navigate = useNavigate();
  return (
    <header className="flex items-center justify-between border-b border-charcoal px-6 py-5 sm:px-10">
      <Link to="/admin/trips" className="flex items-center gap-3">
        <img src={`${process.env.PUBLIC_URL}/holideviser-symbol.svg`} alt="" className="h-7 w-auto" />
        <span className="font-sans text-xs uppercase tracking-[0.24em] text-sand">Admin</span>
      </Link>
      <button
        onClick={() => {
          clearToken();
          navigate("/admin");
        }}
        data-testid="admin-logout"
        className="font-sans text-xs uppercase tracking-[0.18em] text-sand hover:text-teal"
      >
        Sign out
      </button>
    </header>
  );
};

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (search.trim()) params.set("search", search.trim());
    if (sort) params.set("sort", sort);
    try {
      const [s, t] = await Promise.all([fetchStats(), fetchTrips(params.toString())]);
      setStats(s);
      setTrips(t.trips);
    } finally {
      setLoading(false);
    }
  }, [status, search, sort]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid="admin-dashboard">
      <AdminHeader />
      <main className="mx-auto max-w-[1200px] px-6 py-10 sm:px-10">
        <h1 className="font-serif text-3xl font-light sm:text-4xl">Trip requests.</h1>
        <p className="mt-2 font-sans text-sm text-sand">Every Plan My Trip submission, ready to action.</p>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden border border-charcoal sm:grid-cols-4 lg:grid-cols-5" data-testid="admin-stats">
            <div className="bg-charcoal/30 p-5">
              <p className="font-serif text-3xl font-light text-teal">{stats.total}</p>
              <p className="mt-1 font-sans text-[0.65rem] uppercase tracking-[0.16em] text-sand">Total</p>
            </div>
            {STATUS_LIST.map((s) => (
              <button
                key={s}
                onClick={() => setStatus((x) => (x === s ? "" : s))}
                className={`p-5 text-left transition-colors ${status === s ? "bg-teal/10" : "bg-ink hover:bg-charcoal/30"}`}
                data-testid={`stat-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <p className={`font-serif text-3xl font-light ${status === s ? "text-teal" : "text-cream"}`}>
                  {stats.counts[s] ?? 0}
                </p>
                <p className="mt-1 font-sans text-[0.6rem] uppercase tracking-[0.14em] text-sand">{s}</p>
              </button>
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, destination…"
            data-testid="admin-search"
            className="flex-1 rounded-none border border-charcoal bg-charcoal/30 px-4 py-3 font-sans text-sm text-cream placeholder:text-sand/60 focus:border-teal focus:outline-none"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="admin-sort"
            className="rounded-none border border-charcoal bg-charcoal/30 px-4 py-3 font-sans text-sm text-cream focus:border-teal focus:outline-none"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="travel_date">Travel date</option>
            <option value="status">Status</option>
          </select>
          {status && (
            <button onClick={() => setStatus("")} className="font-sans text-xs uppercase tracking-[0.16em] text-teal">
              Clear status: {status}
            </button>
          )}
        </div>

        {/* List */}
        <div className="mt-6 flex flex-col divide-y divide-charcoal border-y border-charcoal" data-testid="admin-trip-list">
          {loading && <p className="py-10 text-center font-sans text-sm text-sand">Loading…</p>}
          {!loading && trips.length === 0 && (
            <p className="py-10 text-center font-sans text-sm text-sand">No trip requests found.</p>
          )}
          {!loading &&
            trips.map((t) => (
              <Link
                key={t.id}
                to={`/admin/trips/${t.id}`}
                data-testid={`trip-row-${t.id}`}
                className="group flex flex-col gap-3 py-5 transition-colors hover:bg-charcoal/20 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl font-light text-cream">{t.customer_name}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="mt-1 font-sans text-sm text-sand">
                    {t.destination || "Undecided"} · from {t.origin} ·{" "}
                    {(t.traveller_type || "—").toString().replace(/^./, (c) => c.toUpperCase())}
                    {t.traveller_count ? ` · ${t.traveller_count} travellers` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-6 text-left sm:text-right">
                  <div>
                    <p className="font-sans text-xs text-cream">{t.travel_dates || t.date_flexibility || "—"}</p>
                    <p className="font-sans text-[0.65rem] uppercase tracking-[0.14em] text-sand">
                      {t.budget_tier || "—"} · {formatDate(t.created_at)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
        </div>
      </main>
    </div>
  );
};

export { AdminHeader };
